import { Component, OnInit } from '@angular/core';
import { DataService } from 'src/app/services/data.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NotificationsService } from '../common/services/notifications.service';
import { UtilitiesService } from '../common/services/utilities.service';
import { Title } from '@angular/platform-browser';
import JSConfetti from 'js-confetti';
import { AuthService } from '../auth/auth.service';
import BuildGameDataSlackNotification from './dtos/BuildGameDataSlackNotification';
import { BuildGameDataEnvs } from './enums/BuildGameDataEnvs';
import { GamedataService } from './services/gamedata.service';
import { SocialUser } from '@abacritt/angularx-social-login';
import { LoggerService } from '../common/services/logger.service';
import { SettingsService } from '../entities/settings/services/settings.service';
import { SpinnerService } from '../common/services/spinner.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-build-gamedata',
  templateUrl: './build-gamedata.component.html',
  styleUrls: ['./build-gamedata.component.sass'],
  providers: [DataService, ConfirmationService],
})
export class BuildGamedataComponent implements OnInit
{
  constructor
  (
    private dataService: DataService,
    private messageService: MessageService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private gamedataService: GamedataService,
    private loggerService: LoggerService,
    private confirmationService: ConfirmationService,
    private utilitiesService: UtilitiesService,
    private settingsService: SettingsService,
    private titleService: Title,
    private spinnerService: SpinnerService
  ) {}

  currentUser: SocialUser = new SocialUser();
  isLoading: boolean = false;
  isInit: boolean = false;
  environmentName: 'development' | 'production' | 'test' | 'local';
  gamedataLogs = {
    enabled: true,
    devGamedata: {},
    qaGamedata: {},
    testGamedata: {},
    prodGamedata: {},
    method: '',
    s3id: null,
  }
  gamedataLogsDef = {
    enabled: true,
    devGamedata: {},
    qaGamedata: {},
    testGamedata: {},
    prodGamedata: {},
    method: '',
    s3id: null,
  }
  successConfetti: Array<any> =
  [
    '🌸',
    '🌷',
    '🌺',
    '💮',
    '💐',
    '🌹',
    '🌼',
    '🌻',
    '🌱',
    '🌴',
    '🌲',
    '🌳',
    '🌿',
  ];
  failedConfetti: Array<any> = ['💩'];
  cmsSettings: any;

  jsConfetti = new JSConfetti();
  get buildEnv(): typeof BuildGameDataEnvs {
    return BuildGameDataEnvs;
  }

  /**
   * Build Gamedata Component Initialization
   */
  async ngOnInit()
  {
    if(window.location.href.includes('prod.cms') ){
      this.environmentName = 'production';
    } else if (window.location.href.includes('test.cms') ) {
      this.environmentName = 'test';
    } else if (window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1')) {
      this.environmentName = 'local';
    } else {
      this.environmentName = 'development';
    }
    this.isInit = true;
    let user = this.authService.getSocialUser();
    this.currentUser = user.currentUser;
    this.titleService.setTitle('Build Gamedata')

    let result = await this.settingsService.getSettingByQuery({ query: { name: 'cms-settings' }});
    this.loggerService.log("CMS Settings: ", result);
    if(result)
    {
      this.cmsSettings = result;
    }
    this.isInit = false;
  }

  /**
   * Build Game data to a desired environment
   *
   * @param env Environmet to push game data
   */
  async onGameDataBuildV3(env: string, isConsecutive: boolean = false) {
    this.spinnerService.loadSpinner();
    this.isLoading = true;
    let request = {
      isConsecutive: isConsecutive,
      exportAll: true
    };

    try {
      const response = await this.gamedataService.buildGamedataV3(env, request);

      console.log("Build Gamedata response: ", response);
      let isGamedataBuildInProgress =  response.isGamedataBuildInProgress;

      if (response && response.success) {
        console.log("Build Gamedata Success response.");
        this.messageService.add({
          severity: 'success',
          summary: 'Building Gamedata',
          detail: 'The Gamedata is building. Please, pay attention to the notifs-gamedata channel on Slack',
        });

        this.isLoading = false;
        this.spinnerService.endSpinner();
        window.scrollTo(0, 0);
        this.jsConfetti.addConfetti({
          emojis: this.successConfetti,
          confettiNumber: 50,
        });
        return { Success: true };
      } else {
        this.jsConfetti.addConfetti({
          emojis: this.failedConfetti,
          confettiNumber: 10,
        });
        this.isLoading = false;
        this.spinnerService.endSpinner();
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: response.error ? response.error  : 'Build Error',
          detail: response.message ? response.message  : 'There was an error in the build.',
        });
        return { Success: false, isGamedataBuildInProgress };
      }

    } catch (error: any) {
      this.isLoading = false;
      this.jsConfetti.addConfetti({
        emojis: this.failedConfetti,
        confettiNumber: 10,
      });
      this.isLoading = false;
      this.spinnerService.endSpinner();

      if (error.status == 504) {
        return { Success: true };
      }
      
      this.messageService.add({
        sticky: true,
        severity: 'error',
        summary: 'Build Error',
        detail: 'There was an error in the build.',
      });
      return { Success: false };
    }
  }

  /**
   * Sends slack notification to flora-gamedata channel
   *
   * @param payload Payload with message details.
   */
  async sendSlackMessage(payload: any)
  {
    await this.notificationsService.sendGameDataBuildNotification(
      payload
    );
  }

  /**
   * Display Build Dev and QA Gamedata Modal
   */
  async displayConfirmBuildDevAndQAModalV3() {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to Build Dev and QA Gamedata V3? QA Build Gamedata will only be executed if Dev gamedata build was successful.',
      header: 'Build DEV + QA Gamedata V3',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await this.onBuildDevAndQAGamedataV3();
      },
      reject: () => {
        return;
      }
    });
  }

  /**
   * Builds Game data for Dev and QA.
   *
   * QA will trigger only if Build Dev Gamedata was successful.
   */
  async onBuildDevAndQAGamedataV3() {
    let response = await this.onGameDataBuildV3('dev', true);

    if (response && response.Success == true) {
      this.messageService.add({
        severity: 'info',
        summary:  'QA Build V3',
        detail: 'QA Build V3 will be executed in the background if dev push response callback is successful. Please see the results in the slack channel.'
      });
    }
    
    this.spinnerService.endSpinner();
  }
}
